import React, { useState, useRef } from 'react';
import { graphql, navigate } from 'gatsby';
import { Button, TextField } from '@lesmills-international/components';
import styled from 'styled-components';
import { Auth } from "@aws-amplify/auth";
import Box from '@mui/material/Box';
import Layout from '../components/common/layout';
import Title from '../components/common/title';

import { ReactComponent as BackIcon } from '../assets/icons/back.svg';

import { LoginLink, Subtitle, Wrapper } from '../components/signup/createAccount/style';
import RecaptchaV3 from '../components/common/recaptchaV3';
import { Link } from '../../../gatsby-theme-engagement/src/components/atoms';
import { formValidator } from '../utils/validator';
import { addErrorInDatadogRum, setLocalStorage } from '../utils/utilities';
import ROUTES from '../../../../src/constants/routes';

const Form = styled(Box)(() => ({
  '& .MuiTextField-root': { marginBottom: '15px', width: '320px' },
  maxWidth: '320px',
  textAlign: 'center',

  '& .MuiInputBase-input:focus': { boxShadow: 'none' },
}));

const FORGOT_PWD_KEY = "forgot-pwd";
const ERRORS = {
  NetworkError: "network_error",
  NotAuthorizedException: "not_authorized_exception",
  UserNotFoundException: "user_not_found_exception",
  ChangePassword: {
    NotAuthorizedException: "not_authorized_exception",
    InvalidPasswordException: "invalid_password_exception",
  },
  FailedRecaptchaV3: "failed recaptcha v3 verification",
};

interface IError {
  email?: string;
}

const ForgotPasswordPage = ({ location, pageContext, data }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<IError>({});
  const emailRef = useRef(null);
  const { lang } = pageContext;

  const { prismicSignUpLayout, prismicForgotPasswordPage: { data: fpData } } = data;

  const signinState = location.state || {};

  const recaptchaRef = useRef();

  const handleDisableLink = (e: { preventDefault: () => void; }) => {
    if (isProcessing) {
      e.preventDefault();
    }
  }

  const getValue = (current: { value: string; }) => current.value

  const handleSubmit = (e: { which?: number; preventDefault: any; }) => {
    e.preventDefault();
    const email = emailRef.current ? getValue(emailRef.current)
          .trim()
          .toLowerCase()
      : "";
    const isValid = formValidator(["REQUIRED", "EMAIL"], email, {
      requiredError: fpData.required_email.text,
      invalidError: fpData.invalid_email.text,
    })

    setErrors({ email: isValid.errorMsg });

    if (isValid.valid) {
      setErrors({ email: '' });
      setIsProcessing(true);
      // Integrate with API
      Auth.forgotPassword(email)
        .then(() => {
          setLocalStorage(FORGOT_PWD_KEY, JSON.stringify({ email }))
          navigate(ROUTES(lang).RESET_PASSWORD)
        })
        .catch(err => {
          setErrors({
            email: data[ERRORS[err.code]]
              ? data[ERRORS[err.code]].text
              : err.message,
          })
          addErrorInDatadogRum(err);
        })
       .finally(() => setIsProcessing(false))
    }
  };

  const handleOnKeyPress = (e: { which: number; preventDefault: () => void; }) => {
    if (e.which === 13) {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  return (
    <Layout
      data={prismicSignUpLayout.data}
      lang={lang}
      pathName="/forgot-password"
      title={fpData.page_title.text}
      omitClose
      scripts={[]}
    >
      <Wrapper>
        <Title
          id="forgot-password-title"
          variant='h5'
          style={{ marginTop: '24px', fontSize: '28px' }}
        >
          {fpData.title.text}
        </Title>
        <Subtitle>{fpData.subtitle.raw[0].text}</Subtitle>
        <Form id="forgot-pwd-form" component="form" noValidate>
          { TextField && <TextField
            label={fpData.email_label.text}
            error={!!errors.email}
            inputRef={emailRef}
            onKeyUp={handleOnKeyPress}
            data-testid="forgot-pwd-email"
            id="email"
            disabled={isProcessing}
            helperText={errors.email}
          /> }
          { Button && <Button
            type="submit"
            id="submit-button"
            onClick={handleSubmit}
            disabled={isProcessing}
            submitting={isProcessing}
            style={{ width: '100%', marginTop: '20px' }}
            data-testid="send-code-btn"
            variant="contained"
            label={fpData.send_reset_code_button_label.text}
          /> }
          <LoginLink id="have-an-account"> 
            <Link to="/login"
              onClick={handleDisableLink}
              state={signinState ? { ...signinState } : {}}
              style={{ marginTop: '20px', fontSize: '16px' }}
              className={`flex justify-center items-center${
                isProcessing ? " disabled" : ""
              }`}>
                <BackIcon />
                <span style={{ paddingLeft: '5px'}}>
                  {fpData.back_to_login.text}
                </span>
            </Link>
          </LoginLink>
        </Form>
        <RecaptchaV3 ref={recaptchaRef} />
      </Wrapper>
    </Layout>
  );
};

export default ForgotPasswordPage;

export const query = graphql`
  query ($lang: String) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
        body1 {
          ... on PrismicSignUpLayoutDataBody1Countries {
            items {
              country_code {
                text
              }
              country_name {
                text
              }
            }
          }
        }
        body2 {
          ... on PrismicSignUpLayoutDataBody2States {
            primary {
              country_name {
                text
              }
              country_code {
                text
              }
            }
            items {
              state_name {
                text
              }
              state_code {
                text
              }
              state_short_name {
                text
              }
            }
          }
        }
      }
    }
    prismicForgotPasswordPage(lang: { eq: $lang }) {
      _previewable
      lang
      data {
        user_not_found_exception {
          text
        }
        back_to_login {
          text
        }
        email_label {
          text
        }
        email_not_found {
          text
        }
        invalid_email {
          text
        }
        page_author {
          text
        }
        page_description {
          text
        }
        page_image {
          alt
          url
        }
        page_keywords {
          text
        }
        page_og_description {
          text
        }
        page_og_title {
          text
        }
        page_og_type {
          text
        }
        page_title {
          text
        }
        page_twitter_card {
          text
        }
        page_twitter_description {
          text
        }
        page_twitter_title {
          text
        }
        required_email {
          text
        }
        send_reset_code_button_label {
          text
        }
        subtitle {
          raw
        }
        title {
          text
        }
      }
    }
  }
`;